import { getToken } from "../storage/authStorage";
import { AccountTreeResponseDTO } from "../models/AccountTreeDTOs";
import {
  DocumentGlType,
  JournalPickerType,
} from "../../domain/entities/AccountTreeEntity";
import { API_BASE_URL } from "../../../config/api";

class APIError extends Error {
  public status: number;
  public data: unknown;

  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

export interface FetchAccountTreeOptions {
  docType: DocumentGlType;
  journalType?: JournalPickerType;
  search?: string;
  accountType?: string;
  limit?: number;
  offset?: number;
  selectedAccountId?: string;
}

export const fetchAccountTree = async (
  options: FetchAccountTreeOptions,
): Promise<AccountTreeResponseDTO> => {
  const {
    docType,
    journalType,
    search,
    accountType,
    limit = 50,
    offset = 0,
    selectedAccountId,
  } = options;

  try {
    const token = getToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const params = new URLSearchParams();
    params.append("doc_type", docType);

    if (docType === "journal") {
      if (!journalType) {
        throw new APIError(
          "journal_type is required when doc_type is journal",
          HTTP_STATUS.INTERNAL_SERVER_ERROR,
        );
      }
      params.append("journal_type", journalType);
    }

    if (search) {
      params.append("search", search);
      params.append("limit", limit.toString());
      params.append("offset", offset.toString());
    } else if (accountType) {
      params.append("account_type", accountType);
      if (selectedAccountId) {
        params.append("selected_account_id", selectedAccountId);
      }
    } else if (selectedAccountId) {
      params.append("selected_account_id", selectedAccountId);
    }

    const response = await fetch(
      `${API_BASE_URL}/api/gl-maps/for-document/tree?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new APIError(
        `Failed to fetch account tree: ${response.statusText}`,
        response.status,
      );
    }

    return response.json();
  } catch (error) {
    console.error("Error fetching account tree:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};
